import type { ApiErrorResponse } from '@manasil/shared';

const BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:8080/v1';
const TOKEN_KEY = 'manasil.admin.token';

/** Mirrors the API's single error shape so screens can switch on `code`, never on text. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export const tokenStore = {
  get: () => localStorage.getItem(TOKEN_KEY),
  set: (token: string) => localStorage.setItem(TOKEN_KEY, token),
  clear: () => localStorage.removeItem(TOKEN_KEY),
};

/** Fired on any 401 so the app can drop back to the login screen from anywhere. */
export const SESSION_EXPIRED = 'manasil:session-expired';

/**
 * Media URLs come back absolute (a bucket) or relative (the API serving the file itself).
 * A relative one is resolved against the API base this client is actually configured with,
 * so it works on localhost and through a tunnel with no server-side configuration.
 */
export function resolveMediaUrl(url: string): string {
  if (/^https?:\/\//i.test(url)) return url;
  return BASE.replace(/\/$/, '') + url;
}

export async function api<T>(
  path: string,
  init: { method?: string; body?: unknown } = {},
): Promise<T> {
  const token = tokenStore.get();
  const res = await fetch(BASE + path, {
    method: init.method ?? 'GET',
    headers: {
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
    },
    ...(init.body !== undefined ? { body: JSON.stringify(init.body) } : {}),
  });

  if (res.status === 204) return undefined as T;

  const json: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    const shape = json as ApiErrorResponse | null;
    if (res.status === 401) {
      tokenStore.clear();
      window.dispatchEvent(new Event(SESSION_EXPIRED));
    }
    throw new ApiError(
      res.status,
      shape?.error?.code ?? 'INTERNAL',
      shape?.error?.message ?? 'Something went wrong',
      shape?.error?.details ?? {},
    );
  }

  return json as T;
}
